import {
  defaultDocumentLanguage,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  ReadyState,
} from "../dbot/common/commonDomain";
import { get, identity } from "lodash";
import { History } from "history";
import {
  adminDashboard,
  boardMemberOverview,
  navigateToProject,
  ScheduleRouteParams,
  scheduleUrl,
  shareholderOverview,
} from "../utils/navigation";
import { prepareMeetingDateForSave } from "../dbot/common/commonBoardMeetingDomain";
import { AccessType } from "../types/enums/AccessType";
import { toLocalDateISOString } from "../utils/Dates";
import { TMessage } from "../i18n/tt";
import {
  DocumentLanguage,
  dualLanguageOptionForOrgNo,
  LocalizedText,
  requiredDocumentLanguagesForOrgNo,
} from "../common/common";
import { DecisionType } from "../common/decisions";
import { IconName } from "../components-v2/iconMapping";
import { DocumentType } from "../common/documents";
import { isNotBlank } from "../utils/strings";
import { CreateMeetingRequest } from "../dbot/project/projectDomain";
import { CompanyDocumentSettings } from "../types/Settings";
import {
  BooleanFieldModel,
  createLanguageOptions,
  DocumentLanguageFieldModel,
  documentLanguageFieldModel,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "../common/commonForms";
import { GeneralMeetingStatus } from "./generalMeeting/electronicGeneralMeetingDomain";
import { CompanyInfo } from "../types/CompanyInfo";
import { DocumentSigningStatus } from "../document/documentApi";
import { UploadedAttachment } from "../fileUpload/fileUploadDomain";

export interface GetMeetingListResponse {
  meetings: MeetingListItem[];
}

// Should migrate GeneralMeetingForm.electronicGeneralMeeting to electronic and merge the two types.
export enum MeetingForm {
  PHYSICAL = "physical_meeting",
  ELECTRONIC = "electronic",
  HYBRID = "hybrid",
  CIRCULATION = "circulation",
  electronicGeneralMeeting = "electronicGeneralMeeting",
}

export enum MeetingPlace {
  companysOffice = "companysOffice",
  inlineAddress = "inlineAddress",
}

export const meetingFormCaptions: { [key in MeetingForm]: TMessage } = {
  [MeetingForm.PHYSICAL]: { key: "documents.meetingForm.physical" },
  [MeetingForm.ELECTRONIC]: { key: "documents.meetingForm.electronic" },
  [MeetingForm.HYBRID]: { key: "documents.meetingForm.hybrid" },
  [MeetingForm.CIRCULATION]: { key: "documents.meetingForm.circulation" },
  [MeetingForm.electronicGeneralMeeting]: { key: "documents.meetingForm.electronic" },
};

export const meetingPlaceCaption = ({
  meetingPlace,
  inlineAddress,
}: {
  meetingPlace?: MeetingPlace;
  inlineAddress?: string;
}): TMessage | undefined => {
  switch (true) {
    case meetingPlace === MeetingPlace.companysOffice:
      return { key: "documents.meetingForm.companys_office" };
    case meetingPlace === MeetingPlace.inlineAddress && isNotBlank(inlineAddress):
      return { translated: inlineAddress };
  }
};

export const meetingFormIcon: { [key in MeetingForm]: IconName } = {
  [MeetingForm.PHYSICAL]: "houseline",
  [MeetingForm.ELECTRONIC]: "webcam",
  [MeetingForm.electronicGeneralMeeting]: "webcam",
  [MeetingForm.HYBRID]: "webcam",
  [MeetingForm.CIRCULATION]: "arrows-clockwise",
};

export type DecisionForMeeting = {
  decisionId: string;
  decisionType: DecisionType;
  titles?: LocalizedText[];
  descriptions?: LocalizedText[];
  isInformational?: boolean;
  attachments?: UploadedAttachment[];
};

export type DocumentForMeeting = {
  documentId: string;
  documentType: DocumentType;
  sentAt?: string;
  signedAt?: string;
  signingStatus?: DocumentSigningStatus;
};

export interface MeetingListItem {
  id: string;
  projectId: string;
  projectName: string;
  meetingType: MeetingType;
  meetingForm: MeetingForm;
  decisions: DecisionForMeeting[];
  documents: DocumentForMeeting[];
  meetingStartDate?: string;
  meetingStartTime?: string;
  meetingEndDate?: string;
  meetingEndTime?: string;
  hasSentNoticeOfBoardMeeting: boolean;
  hasSentNoticeOfGeneralMeeting: boolean;
  meetingPlace?: MeetingPlace;
  inlineAddress?: string;
  meetingLink?: string;
  isExtraordinary?: boolean;
  meetingNumber?: string;
  meetingDescription?: LocalizedText[];
  numberOfPermissions?: number;
  isElectronicMeeting?: boolean;
  generalMeetingStatus?: GeneralMeetingStatus;
  noticeForShareholderId?: string;
  isPlaceholder?: boolean;
}

export enum MeetingType {
  BOARD_MEETING = "BOARD_MEETING",
  GENERAL_MEETING = "GENERAL_MEETING",
}

export interface NewMeetingDialogModel {
  projectName: StringFieldModel;
  meetingForm: RadioFieldModel<MeetingForm>;
  meetingStartDate: LocalDateFieldModel;
  meetingStartTime: TimeFieldModel;
  meetingEndTime: TimeFieldModel;
  documentSelection: RadioFieldModel<DocumentSelection>;
  meetingOptions: OptionFieldModel<MeetingOption>;
  meetingDocumentLanguage: DocumentLanguageFieldModel;
  onMeetingOptionChange: (option: MeetingOption, checked: boolean) => void;
  ordinaryGeneralMeeting: RadioFieldModel<boolean>;
  displayElectronicGMDialog: BooleanFieldModel;
  companyInfo: CompanyInfo | null;
}

export enum MeetingOption {
  NOTICE = "NOTICE",
  MEETING = "MEETING",
  MINUTES = "MINUTES",
}

export enum DocumentSelection {
  NOTICE_AND_MINUTES = "NOTICE_AND_MINUTES",
  MINUTES_ONLY = "MINUTES_ONLY",
}

export function isValidNewMeeting(model: NewMeetingDialogModel): boolean {
  return (
    model.projectName.isValid &&
    model.meetingStartDate.isValid &&
    model.meetingStartTime.isValid &&
    model.meetingEndTime.isValid
  );
}

export const editMeeting = (orgNo: string, history: History) => {
  return (projectId: string) => {
    navigateToProject({ orgNo, projectId, history });
  };
};

const documentSelectionCaptions: { [key in DocumentSelection]: TMessage } = {
  [DocumentSelection.NOTICE_AND_MINUTES]: { key: "new-meeting.notice-and-minutes" },
  [DocumentSelection.MINUTES_ONLY]: { key: "new-meeting.minutes-only" },
};

const meetingOptionsCaptions: { [key in MeetingOption]: TMessage } = {
  [MeetingOption.NOTICE]: { key: "meetings.notice" },
  [MeetingOption.MEETING]: { key: "meeting" },
  [MeetingOption.MINUTES]: { key: "meetings.minutes" },
};

export const getDefaultLanguage = (
  orgNo: string,
  documentSettings: CompanyDocumentSettings | undefined,
  languageOptions: DocumentLanguage[],
) => {
  const option = mapLocalesToDocumentLanguage(
    documentSettings?.defaultDocumentLanguages || defaultDocumentLanguage(orgNo),
  );
  if (option && !languageOptions.includes(option)) {
    return dualLanguageOptionForOrgNo(orgNo);
  }
  return option;
};

export const createNewMeetingDialogModel = (
  orgNo: string,
  projectName: string,
  meetingType: MeetingType,
  companyInfo: CompanyInfo | null = null,
  documentSettings: CompanyDocumentSettings | undefined,
  meetingDate: string = toLocalDateISOString(new Date()),
): NewMeetingDialogModel => {
  const languageOptions = createLanguageOptionsForMeetingType(meetingType, orgNo);
  const defaultLanguage = getDefaultLanguage(orgNo, documentSettings, languageOptions);
  const meetingOptions = new OptionFieldModel<MeetingOption>({
    value: [MeetingOption.NOTICE, MeetingOption.MINUTES],
    options: Object.values(MeetingOption),
    labelFn: (option) => meetingOptionsCaptions[option],
    idFn: (option) => String(option),
  });
  const displayElectronicGMDialog = new BooleanFieldModel({ value: false });
  return {
    projectName: new StringFieldModel({
      value: projectName,
      required: true,
    }),
    meetingForm: new RadioFieldModel<MeetingForm>({
      idFn: identity,
      labelFn: (value) => meetingFormCaptions[value],
      options: Object.values(MeetingForm).filter((v) => v !== MeetingForm.electronicGeneralMeeting),
      value: MeetingForm.HYBRID,
    }),
    meetingStartDate: new LocalDateFieldModel({ required: true, value: meetingDate }),
    meetingStartTime: new TimeFieldModel({ value: "12:00", required: true }),
    meetingEndTime: new TimeFieldModel({ value: "14:00", required: true }),
    documentSelection: new RadioFieldModel<DocumentSelection>({
      idFn: identity,
      labelFn: (value) => documentSelectionCaptions[value],
      options: Object.values(DocumentSelection),
      value: DocumentSelection.NOTICE_AND_MINUTES,
    }),
    meetingDocumentLanguage: documentLanguageFieldModel({
      orgNo: orgNo,
      value: defaultLanguage,
      options: languageOptions,
    }),
    meetingOptions,
    onMeetingOptionChange: (option, checked) => {
      meetingOptions.toggle(option);
      if (option === MeetingOption.MEETING && checked) {
        meetingOptions.select(MeetingOption.NOTICE);
        displayElectronicGMDialog.setValue(true);
      } else if (option === MeetingOption.NOTICE && !checked) {
        meetingOptions.unselect(MeetingOption.MEETING);
      }
    },
    ordinaryGeneralMeeting: new RadioFieldModel({
      value: true,
      idFn: String,
      options: [true, false],
      labelFn: (val) => (val ? { key: "meetings.ordinary" } : { key: "meetings.extraordinary" }),
    }),
    displayElectronicGMDialog,
    companyInfo,
  };
};

export const createLanguageOptionsForMeetingType = (
  meetingType: MeetingType,
  orgNo: string,
): DocumentLanguage[] => {
  if (meetingType === MeetingType.GENERAL_MEETING) {
    return requiredDocumentLanguagesForOrgNo(orgNo);
  } else {
    return createLanguageOptions({ orgNo });
  }
};

export const createMeetingRequest = (
  orgNo: string,
  meetingType: MeetingType,
  model: NewMeetingDialogModel,
  duplicateProjectId?: string,
): CreateMeetingRequest => {
  return {
    orgNo,
    meetingType,
    projectName: model.projectName.validatedValue,
    meetingDocumentSelection: model.documentSelection.value,
    meetingForm: model.meetingForm.value,
    meetingOptions: model.meetingOptions.selected,
    meetingDocumentLanguage: mapDocumentLanguageToLocales(model.meetingDocumentLanguage.value),
    duplicateProjectId: duplicateProjectId,
    ordinaryGeneralMeeting: model.ordinaryGeneralMeeting.value,
    ...prepareMeetingDateForSave(model),
  };
};

export const newMeetingCaptions: { [key in MeetingType]: TMessage } = {
  [MeetingType.BOARD_MEETING]: { key: "meetings.new-board-meeting" },
  [MeetingType.GENERAL_MEETING]: { key: "meetings.new-general-meeting" },
};

export const openDashboardForAccessType = (
  accessType: AccessType,
  orgNo: string,
  shareholderId?: number,
): { label: TMessage; url: string } | undefined => {
  switch (accessType) {
    case AccessType.SUPERADMIN:
    case AccessType.ADMIN:
      return { label: { key: "user-menu.open-dashboard" }, url: adminDashboard(orgNo) };
    case AccessType.BOARD_MEMBER:
      return { label: { key: "user-menu.open-board-portal" }, url: boardMemberOverview(orgNo) };
    case AccessType.SHAREHOLDER:
      return {
        label: { key: "user-menu.open-shareholder-portal" },
        url: shareholderOverview(orgNo, shareholderId ?? 0),
      };
  }
};

export type MeetingNavigationState = {
  type: "meeting-navigation-state";
  from: "schedule";
  params: ScheduleRouteParams;
};

const isMeetingNavigationState = (state: unknown): state is MeetingNavigationState =>
  get(state, "type") === "meeting-navigation-state";

export const getBackUrlFromHistory = (history: History) => {
  const state = history.location.state;
  if (!state || !isMeetingNavigationState(state)) {
    return undefined;
  }
  switch (state.from) {
    case "schedule":
      return scheduleUrl(state.params);
  }
};

export const meetingTypeCaption = ({
  meetingType,
  isExtraordinary,
}: {
  meetingType: MeetingType;
  isExtraordinary?: boolean;
}): TMessage => {
  switch (meetingType) {
    case MeetingType.BOARD_MEETING:
      return { key: "meetings.board-meeting" };
    case MeetingType.GENERAL_MEETING:
      return isExtraordinary
        ? { key: "meetings.extraordinary-general-meeting" }
        : { key: "meetings.general-meeting" };
  }
};

export type PreviewState =
  | ReadyState
  | {
      type: "preview";
      filename: string | TMessage;
      file: { url: string } | { data: ArrayBuffer | Blob };
    };

export const meetingTypeToDocumentTypes = (meetingType: MeetingType): DocumentType[] => {
  switch (meetingType) {
    case MeetingType.BOARD_MEETING:
      return [DocumentType.notice_board_meeting, DocumentType.board_meeting];
    case MeetingType.GENERAL_MEETING:
      return [DocumentType.notice_general_meeting, DocumentType.general_meeting];
  }
};
